import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./SubTasks.css";
import RightPopup from "../../../Shared/RightPopup/RightPopup";
import SubTaskSelect from "./SubTaskSelect/SubTaskSelect";
import PlusIcon from "../../../../assets/taskIconBtn.svg";
import { IFunctionPermissions } from "../../../../common/api_interfaces/role/IFunctionPermissions";
import { AppContext } from "../../../../App";
import { getOptionsQueryParams, IOptionsQueryParams } from "../../../../helpers/paramsAutoFiller";
import { ISubTask } from "../../../../common/api_interfaces/task/ISubTask";
import { TaskContext } from "../../../../contexts/TaskContext";
import TaskAddEditForm from "../../TaskAddEditForm/TaskAddEditForm";
import Button from "../../../UiLib/Button/Button";
import useOutsideAlerter from "../../../../hooks/useOutsideAlert";
import { IProjectMinimal } from "../../../../common/api_interfaces/project/ICardProjectProject";

import { ReactComponent as Pluss } from "../../../../assets/images/addDefault.svg";

interface ISubTasksProps {
    subtaskList?: ISubTask[];
    allowedProjects?: IProjectMinimal[];
    setSubTasks?: (tasks: ISubTask[]) => void;
    taskId: number;
    userAccess: IFunctionPermissions[];
    optionsQueryParams: IOptionsQueryParams;
}

const SubTasks: FC<ISubTasksProps> = ({ subtaskList, allowedProjects, taskId, userAccess, optionsQueryParams }) => {
    const taskContext = useContext(TaskContext);
    const { showToast } = useContext(AppContext);

    const [showSelectPopup, setShowSelectPopup] = useState(false);
    const [showAddPopup, setShowAddPopup] = useState(false);
    const [showCreatePopup, setShowCreatePopup] = useState(false);

    
    const outsideAlerterRef = useOutsideAlerter<HTMLDivElement>(() => {setShowAddPopup(false);});

    // Определяем права доступа
    const { showAddSubtaskButton, canCreateTask } = useMemo(() => {
        const taskActionAccess = userAccess.find((access) => access.functionCode === "TaskAction");
        
        if (!taskActionAccess) {
            return { showAddSubtaskButton: false, canCreateTask: false };
        }

        const hasEditPermission = taskActionAccess.permissions.some(
            (permission) => permission.permissionCode === "edit" && permission.isGranted
        );

        const hasCreatePermission = taskActionAccess.permissions.some(
            (permission) => permission.permissionCode === "create" && permission.isGranted
        );

        return {
            showAddSubtaskButton: hasEditPermission,
            canCreateTask: hasCreatePermission
        };
    }, [userAccess]);

    // Проверка параметров URL для открытия попапа создания
    useEffect(() => {
        const options = getOptionsQueryParams(window.location.search);
        if (options.page === "task" && options.entity === "sub-task-form" && options.action === "create") {
            setShowCreatePopup(true);
        }
    }, []);

    const hadleAddClick = useCallback(() => {
        setShowAddPopup(prev => !prev);
    }, []);

    const onTaskCreate = useCallback(() => {
        setShowCreatePopup(false);
        taskContext.loadSubTasks();
    }, [taskContext.loadSubTasks]);

    const onShowSubtaskSelect = useCallback(() => {
        setShowSelectPopup(prev => !prev);
        setShowAddPopup(false);
    }, []);

    const onShowSubtaskCreate = useCallback(() => {
        setShowAddPopup(false);
        if (canCreateTask) {
            setShowCreatePopup(true);
        } else {
            showToast("У вас нет прав для создания задачи!");
        }
    }, [canCreateTask, showToast]);

    // Фильтрация подзадач
    const filteredSubtasks = useMemo(() => {
        if (!subtaskList || !taskContext.taskInformation) return [];

        return subtaskList.filter((task) => 
            task.id !== taskContext.taskInformation?.id && 
            task.parentId === taskContext.taskInformation?.id
        );
    }, [subtaskList, taskContext.taskInformation]);

    if (!taskContext.taskInformation) {
        return null;
    }

    return (
        <div className="right-menu-subtask-component" ref={outsideAlerterRef}>
            <div className="right-menu-subtask-head">
                <h2 className="p_17">
                    Подзадачи - {filteredSubtasks.length}
                </h2>
                {showAddSubtaskButton && (
                    <div>
                        <Button
                            id="button-circle-add"
                            image={<Pluss/>}
                            callBack={hadleAddClick}
                        />
                        {showAddPopup && (
                            <div className="small-add-option-popup keep-popup">
                                <div className="add-option-text keep-popup" onClick={onShowSubtaskCreate}>
                                    Создать
                                </div>
                                <div className="add-option-text keep-popup" onClick={onShowSubtaskSelect}>
                                    Выбрать
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
            
            <div className="right-menu-subtask-item-container">
                {filteredSubtasks.map((subTask) => (
                    <Link 
                        key={subTask.id} 
                        to={`/task/${subTask.id}`} 
                        className="right-menu-subtask-item"
                    >
                        [{subTask.number}] {subTask.name}
                    </Link>
                ))}
            </div>

            {showCreatePopup && (
                <div className="full-screen-overlay">
                    <div className="popup-form-background" onClick={(e) => e.stopPropagation()}>
                        <TaskAddEditForm
                            responsibleRequired={taskContext.projectSettings.responsibleRequired}
                            mode="create"
                            taskId={undefined}
                            closeFunc={() => setShowCreatePopup(false)}
                            onTaskAction={onTaskCreate}
                            parentTaskId={taskId}
                            projectId={taskContext.taskInformation.projectId}
                            optionsQueryParams={{
                                page: optionsQueryParams.page,
                                entity: "sub-task-form",
                                action: "create",
                                entityId: optionsQueryParams.entityId,
                            }}
                        />
                    </div>
                </div>
            )}

            {showSelectPopup && (
                <RightPopup
                    onClose={() => setShowSelectPopup(false)}
                    name="Добавление подзадачи"
                >
                    <SubTaskSelect />
                </RightPopup>
            )}
        </div>
    );
};

export default SubTasks;